import React, { useState, useEffect, useCallback } from 'react';
import serverService from '../services/serverService';
import { Card, Button, Modal } from '../components/common';

/**
 * Server Monitor - 서버 모니터링 대시보드
 */
const ServerMonitor = ({ activeSection: initialActiveSection = 'system' }) => {
  const [activeSection, setActiveSection] = useState(initialActiveSection);
  const [systemInfo, setSystemInfo] = useState(null);
  const [pm2Processes, setPm2Processes] = useState([]);
  const [nginxStatus, setNginxStatus] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [logs, setLogs] = useState('');
  const [logType, setLogType] = useState('pm2-error');
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [processLogs, setProcessLogs] = useState('');
  const [loading, setLoading] = useState({
    system: false,
    pm2: false,
    nginx: false,
    db: false,
    logs: false
  });
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [restartModal, setRestartModal] = useState({ open: false, process: null });
  const [restartLoading, setRestartLoading] = useState(false);

  // 시스템 정보 로드
  const loadSystemInfo = useCallback(async () => {
    setLoading(prev => ({ ...prev, system: true }));
    setError(null);
    try {
      const result = await serverService.getSystemInfo();
      if (result.success) {
        setSystemInfo(result.data);
      } else {
        setError(result.error || '시스템 정보를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('System info error:', err);
      setError(`시스템 정보를 불러오는데 실패했습니다: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, system: false }));
    }
  }, []);

  // PM2 프로세스 로드
  const loadPM2Processes = useCallback(async () => {
    setLoading(prev => ({ ...prev, pm2: true }));
    try {
      const result = await serverService.getPM2Processes();
      if (result.success) {
        setPm2Processes(result.data);
      } else {
        console.error('PM2 processes error:', result.error);
      }
    } catch (err) {
      console.error('PM2 processes error:', err);
      setError(`PM2 프로세스 정보를 불러오는데 실패했습니다: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, pm2: false }));
    }
  }, []);

  // Nginx 상태 로드
  const loadNginxStatus = useCallback(async () => {
    setLoading(prev => ({ ...prev, nginx: true }));
    try {
      const result = await serverService.getNginxStatus();
      if (result.success) {
        setNginxStatus(result.data);
      } else {
        console.error('Nginx status error:', result.error);
      }
    } catch (err) {
      console.error('Nginx status error:', err);
      setError(`Nginx 상태를 불러오는데 실패했습니다: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, nginx: false }));
    }
  }, []);

  // DB 정보 로드
  const loadDbInfo = useCallback(async () => {
    setLoading(prev => ({ ...prev, db: true }));
    try {
      const result = await serverService.getDatabaseInfo();
      if (result.success) {
        setDbInfo(result.data);
      } else {
        console.error('Database info error:', result.error);
      }
    } catch (err) {
      console.error('Database info error:', err);
      setError(`데이터베이스 정보를 불러오는데 실패했습니다: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, db: false }));
    }
  }, []);

  // 로그 로드
  const loadLogs = useCallback(async (type) => {
    setLoading(prev => ({ ...prev, logs: true }));
    try {
      const result = await serverService.getLogs(type);
      if (result.success) {
        setLogs(result.data.logs);
      }
    } catch (err) {
      setLogs('로그를 불러오는데 실패했습니다.');
    } finally {
      setLoading(prev => ({ ...prev, logs: false }));
    }
  }, []);

  // PM2 프로세스 재시작 모달 열기
  const handleRestartClick = (proc) => {
    setRestartModal({ open: true, process: proc });
  };

  // PM2 프로세스 재시작 확인
  const handleRestartConfirm = async () => {
    if (!restartModal.process) return;

    setRestartLoading(true);
    try {
      const result = await serverService.restartPM2Process(restartModal.process.name);
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        setRestartModal({ open: false, process: null });
        loadPM2Processes();
      } else {
        setMessage({ type: 'error', text: result.error || '재시작 실패' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '프로세스 재시작 중 오류가 발생했습니다.' });
    } finally {
      setRestartLoading(false);
    }
  };

  // PM2 프로세스 로그 보기
  const handleViewProcessLogs = async (name) => {
    setSelectedProcess(name);
    try {
      const result = await serverService.getPM2Logs(name);
      if (result.success) {
        setProcessLogs(result.data.logs);
      }
    } catch (err) {
      setProcessLogs('로그를 불러오는데 실패했습니다.');
    }
  };

  // activeSection prop 변경 시 동기화
  useEffect(() => {
    setActiveSection(initialActiveSection);
  }, [initialActiveSection]);

  // 초기 로드
  useEffect(() => {
    loadSystemInfo();
    loadPM2Processes();
    loadNginxStatus();
    loadDbInfo();
  }, [loadSystemInfo, loadPM2Processes, loadNginxStatus, loadDbInfo]);

  // 자동 새로고침
  useEffect(() => {
    if (refreshInterval) {
      const interval = setInterval(() => {
        loadSystemInfo();
        loadPM2Processes();
      }, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, loadSystemInfo, loadPM2Processes]);

  // 메시지 자동 숨김
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // 바이트 포맷팅
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 프로세스 업타임 포맷팅
  const formatProcessUptime = (uptime) => {
    if (!uptime) return '-';
    const now = Date.now();
    const diff = now - uptime;
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);

    if (days > 0) return `${days}일 ${hours}시간`;
    if (hours > 0) return `${hours}시간 ${minutes}분`;
    return `${minutes}분`;
  };

  // 탭 정보
  const tabs = [
    { key: 'system', label: '시스템', icon: '🖥️' },
    { key: 'pm2', label: 'PM2 프로세스', icon: '⚙️' },
    { key: 'services', label: '서비스', icon: '🌐' },
    { key: 'database', label: '데이터베이스', icon: '🗄️' },
    { key: 'logs', label: '로그', icon: '📋' }
  ];

  // 온라인/오프라인 통계
  const onlineProcesses = pm2Processes.filter(p => p.status === 'online').length;
  const totalProcesses = pm2Processes.length;

  return (
    <div className="flex flex-col gap-6 max-w-[1400px] mx-auto max-md:gap-4">
      {/* 메시지 */}
      {message && (
        <div className={`py-3 px-4 rounded-lg text-sm font-medium ${
          message.type === 'success' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
        }`}>
          {message.text}
        </div>
      )}

      {error && (
        <div className="py-3 px-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* 헤더 */}
      <div className="flex justify-between items-start gap-4 max-md:flex-col max-md:gap-4">
        <div>
          <h1 className="text-[28px] font-bold text-gray-900 m-0 mb-1 max-md:text-2xl">서버 모니터링</h1>
          <p className="text-sm text-gray-600 m-0">서버 상태 및 프로세스를 관리합니다</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 max-md:flex-wrap">
          <select
            value={refreshInterval || ''}
            onChange={(e) => setRefreshInterval(e.target.value ? parseInt(e.target.value) : null)}
            className="py-2 px-3 border border-gray-200 rounded-lg text-[13px] text-gray-900 bg-white cursor-pointer transition-all hover:border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
          >
            <option value="">수동 새로고침</option>
            <option value="5">5초마다</option>
            <option value="10">10초마다</option>
            <option value="30">30초마다</option>
            <option value="60">1분마다</option>
          </select>
          <Button
            variant="primary"
            onClick={() => {
              loadSystemInfo();
              loadPM2Processes();
              loadNginxStatus();
              loadDbInfo();
            }}
          >
            새로고침
          </Button>
        </div>
      </div>

      {/* 상태 요약 */}
      <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-md:grid-cols-2 max-md:gap-3 max-sm:grid-cols-1">
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${
            activeSection === 'pm2' ? 'bg-blue-500 border-blue-600' : 'bg-white border-gray-200'
          }`}
          onClick={() => setActiveSection('pm2')}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${activeSection === 'pm2' ? 'grayscale brightness-[10]' : ''}`}>✅</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${activeSection === 'pm2' ? 'text-white' : 'text-gray-900'}`}>
              {onlineProcesses}/{totalProcesses}
            </span>
            <span className={`text-[13px] ${activeSection === 'pm2' ? 'text-white' : 'text-gray-600'}`}>
              온라인 프로세스
            </span>
          </div>
        </div>
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${
            activeSection === 'system' ? 'bg-blue-500 border-blue-600' : 'bg-white border-gray-200'
          }`}
          onClick={() => setActiveSection('system')}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${activeSection === 'system' ? 'grayscale brightness-[10]' : ''}`}>💻</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${activeSection === 'system' ? 'text-white' : 'text-gray-900'}`}>
              {systemInfo?.cpu?.usage || '-'}%
            </span>
            <span className={`text-[13px] ${activeSection === 'system' ? 'text-white' : 'text-gray-600'}`}>
              CPU 사용량
            </span>
          </div>
        </div>
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${
            activeSection === 'system' ? 'bg-blue-500 border-blue-600' : 'bg-white border-gray-200'
          }`}
          onClick={() => setActiveSection('system')}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${activeSection === 'system' ? 'grayscale brightness-[10]' : ''}`}>📊</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${activeSection === 'system' ? 'text-white' : 'text-gray-900'}`}>
              {systemInfo?.memory?.percentage || '-'}%
            </span>
            <span className={`text-[13px] ${activeSection === 'system' ? 'text-white' : 'text-gray-600'}`}>
              메모리 사용량
            </span>
          </div>
        </div>
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${
            activeSection === 'database' ? 'bg-blue-500 border-blue-600' : 'bg-white border-gray-200'
          }`}
          onClick={() => setActiveSection('database')}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${activeSection === 'database' ? 'grayscale brightness-[10]' : ''}`}>🗄️</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${activeSection === 'database' ? 'text-white' : 'text-gray-900'}`}>
              {dbInfo?.fileSizeFormatted || '-'}
            </span>
            <span className={`text-[13px] ${activeSection === 'database' ? 'text-white' : 'text-gray-600'}`}>
              DB 크기
            </span>
          </div>
        </div>
      </div>

      {/* 섹션 탭 */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto max-md:gap-0">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`flex items-center gap-2 py-3 px-5 bg-transparent border-none text-sm font-medium cursor-pointer border-b-2 -mb-px transition-all whitespace-nowrap max-md:py-2.5 max-md:px-4 max-md:text-[13px] ${
              activeSection === tab.key
                ? 'text-blue-500 border-blue-500 font-semibold'
                : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-slate-100'
            }`}
            onClick={() => {
              setActiveSection(tab.key);
              if (tab.key === 'logs') loadLogs(logType);
            }}
          >
            <span className="text-base">{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* 시스템 모니터링 */}
      {activeSection === 'system' && (
        <div className="min-h-[300px]">
          {loading.system ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
              <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
              <p className="text-gray-600 text-sm">시스템 정보 로딩 중...</p>
            </div>
          ) : systemInfo ? (
            <>
              {/* 리소스 게이지 */}
              <div className="grid grid-cols-3 gap-4 mb-6 max-lg:grid-cols-2 max-md:grid-cols-1">
                <Card className="bg-white">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-[15px] font-semibold text-gray-900">CPU</span>
                    <span className="text-2xl font-bold text-blue-500">{systemInfo.cpu.usage}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded overflow-hidden mb-3">
                    <div
                      className={`h-full rounded transition-all duration-400 ${
                        parseFloat(systemInfo.cpu.usage) > 80 ? 'bg-red-500' : parseFloat(systemInfo.cpu.usage) > 50 ? 'bg-amber-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${systemInfo.cpu.usage}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>{systemInfo.cpu.cores} 코어</span>
                    <span className="max-w-[60%] whitespace-nowrap overflow-hidden text-ellipsis">{systemInfo.cpu.model}</span>
                  </div>
                </Card>

                <Card className="bg-white">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-[15px] font-semibold text-gray-900">메모리</span>
                    <span className="text-2xl font-bold text-blue-500">{systemInfo.memory.percentage}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded overflow-hidden mb-3">
                    <div
                      className={`h-full rounded transition-all duration-400 ${
                        parseFloat(systemInfo.memory.percentage) > 80 ? 'bg-red-500' : parseFloat(systemInfo.memory.percentage) > 50 ? 'bg-amber-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${systemInfo.memory.percentage}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>{formatBytes(systemInfo.memory.used)} / {formatBytes(systemInfo.memory.total)}</span>
                  </div>
                </Card>

                <Card className="bg-white">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-[15px] font-semibold text-gray-900">디스크</span>
                    <span className="text-2xl font-bold text-blue-500">{systemInfo.disk.percentage}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded overflow-hidden mb-3">
                    <div
                      className={`h-full rounded transition-all duration-400 ${
                        systemInfo.disk.percentage > 80 ? 'bg-red-500' : systemInfo.disk.percentage > 50 ? 'bg-amber-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${systemInfo.disk.percentage}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>{formatBytes(systemInfo.disk.used)} / {formatBytes(systemInfo.disk.total)}</span>
                  </div>
                </Card>
              </div>

              {/* 시스템 정보 */}
              <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-md:grid-cols-2 max-sm:grid-cols-1">
                <Card className="bg-white text-center">
                  <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">업타임</div>
                  <div className="text-[15px] font-semibold text-gray-900">{systemInfo.uptime.formatted}</div>
                </Card>
                <Card className="bg-white text-center">
                  <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">Node.js</div>
                  <div className="text-[15px] font-semibold text-gray-900">{systemInfo.node.version}</div>
                </Card>
                <Card className="bg-white text-center">
                  <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">호스트명</div>
                  <div className="text-[15px] font-semibold text-gray-900">{systemInfo.platform.hostname}</div>
                </Card>
                <Card className="bg-white text-center">
                  <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">OS</div>
                  <div className="text-[15px] font-semibold text-gray-900">{systemInfo.platform.type} {systemInfo.platform.arch}</div>
                </Card>
              </div>
            </>
          ) : (
            <Card className="bg-white">
              <div className="flex flex-col items-center justify-center py-12 px-6 gap-3">
                <span className="text-5xl leading-none">⚠️</span>
                <p className="text-sm text-gray-600 m-0">시스템 정보를 불러올 수 없습니다.</p>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* PM2 프로세스 관리 */}
      {activeSection === 'pm2' && (
        <div className="min-h-[300px]">
          {loading.pm2 ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
              <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
              <p className="text-gray-600 text-sm">PM2 프로세스 로딩 중...</p>
            </div>
          ) : pm2Processes.length > 0 ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4 max-md:grid-cols-1">
              {pm2Processes.map((proc) => (
                <Card key={proc.pm_id} className={`bg-white border-l-[3px] ${
                  proc.status === 'online' ? 'border-l-green-500' : 'border-l-red-500'
                }`}>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-base font-semibold text-gray-900">{proc.name}</span>
                    <span className={`py-1 px-3 rounded-xl text-xs font-medium ${
                      proc.status === 'online' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                    }`}>
                      {proc.status === 'online' ? '실행 중' : proc.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4 max-md:grid-cols-2">
                    <div className="text-center p-2 bg-gray-50 rounded-md">
                      <span className="block text-[10px] font-medium text-gray-600 uppercase mb-0.5">PID</span>
                      <span className="text-sm font-semibold text-gray-900">{proc.pid || '-'}</span>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-md">
                      <span className="block text-[10px] font-medium text-gray-600 uppercase mb-0.5">CPU</span>
                      <span className="text-sm font-semibold text-gray-900">{proc.cpu}%</span>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-md">
                      <span className="block text-[10px] font-medium text-gray-600 uppercase mb-0.5">메모리</span>
                      <span className="text-sm font-semibold text-gray-900">{formatBytes(proc.memory)}</span>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-md">
                      <span className="block text-[10px] font-medium text-gray-600 uppercase mb-0.5">업타임</span>
                      <span className="text-sm font-semibold text-gray-900">{formatProcessUptime(proc.uptime)}</span>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-md">
                      <span className="block text-[10px] font-medium text-gray-600 uppercase mb-0.5">재시작</span>
                      <span className="text-sm font-semibold text-gray-900">{proc.restarts}회</span>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-4 border-t border-gray-100">
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => handleRestartClick(proc)}
                    >
                      재시작
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleViewProcessLogs(proc.name)}
                    >
                      로그
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-white">
              <div className="flex flex-col items-center justify-center py-12 px-6 gap-3">
                <span className="text-5xl leading-none">⚙️</span>
                <p className="text-sm text-gray-600 m-0">PM2 프로세스가 없습니다.</p>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* 서비스 상태 */}
      {activeSection === 'services' && (
        <div className="min-h-[300px]">
          {loading.nginx ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
              <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
              <p className="text-gray-600 text-sm">서비스 상태 로딩 중...</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-2 max-md:grid-cols-1">
              <Card className={`flex items-center gap-4 bg-white border-l-[3px] ${
                nginxStatus?.isRunning ? 'border-l-green-500' : 'border-l-red-500'
              }`}>
                <div className="text-[32px] leading-none">🌐</div>
                <div className="flex-1">
                  <div className="text-base font-semibold text-gray-900">Nginx</div>
                  <div className="text-xs text-gray-600">v{nginxStatus?.version || '-'}</div>
                </div>
                <div className={`py-1.5 px-3 rounded-2xl text-xs font-medium ${
                  nginxStatus?.isRunning ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                }`}>
                  {nginxStatus?.isRunning ? '실행 중' : '중지됨'}
                </div>
              </Card>

              <Card className={`flex items-center gap-4 bg-white border-l-[3px] ${
                pm2Processes.some(p => p.name === 'ilouli-main-api' && p.status === 'online') ? 'border-l-green-500' : 'border-l-red-500'
              }`}>
                <div className="text-[32px] leading-none">⚙️</div>
                <div className="flex-1">
                  <div className="text-base font-semibold text-gray-900">Backend API</div>
                  <div className="text-xs text-gray-600">{systemInfo?.node?.version || '-'}</div>
                </div>
                <div className={`py-1.5 px-3 rounded-2xl text-xs font-medium ${
                  pm2Processes.some(p => p.name === 'ilouli-main-api' && p.status === 'online') ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                }`}>
                  {pm2Processes.some(p => p.name === 'ilouli-main-api' && p.status === 'online') ? '실행 중' : '중지됨'}
                </div>
              </Card>

              <Card className="flex items-center gap-4 bg-white border-l-[3px] border-l-green-500">
                <div className="text-[32px] leading-none">🗄️</div>
                <div className="flex-1">
                  <div className="text-base font-semibold text-gray-900">SQLite</div>
                  <div className="text-xs text-gray-600">{dbInfo?.fileSizeFormatted || '-'}</div>
                </div>
                <div className="py-1.5 px-3 rounded-2xl text-xs font-medium bg-green-100 text-green-600">정상</div>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* 데이터베이스 */}
      {activeSection === 'database' && (
        <div className="min-h-[300px]">
          {loading.db ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
              <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
              <p className="text-gray-600 text-sm">데이터베이스 정보 로딩 중...</p>
            </div>
          ) : dbInfo ? (
            <>
              {/* DB 요약 */}
              <div className="grid grid-cols-3 gap-4 mb-6 max-md:grid-cols-1">
                <Card className="bg-white text-center">
                  <span className="block text-[28px] font-bold text-blue-500 mb-1">{dbInfo.fileSizeFormatted}</span>
                  <span className="text-[13px] text-gray-600">DB 크기</span>
                </Card>
                <Card className="bg-white text-center">
                  <span className="block text-[28px] font-bold text-blue-500 mb-1">{dbInfo.recentSignups?.today || 0}</span>
                  <span className="text-[13px] text-gray-600">오늘 가입</span>
                </Card>
                <Card className="bg-white text-center">
                  <span className="block text-[28px] font-bold text-blue-500 mb-1">{dbInfo.recentSignups?.thisWeek || 0}</span>
                  <span className="text-[13px] text-gray-600">이번 주 가입</span>
                </Card>
              </div>

              {/* 테이블별 레코드 수 */}
              <Card className="bg-white">
                <h4 className="text-base font-semibold text-gray-900 m-0 mb-4">테이블별 레코드 수</h4>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2 max-md:grid-cols-1">
                  {Object.entries(dbInfo.tables).map(([table, count]) => (
                    <div key={table} className="flex justify-between items-center py-3 px-4 bg-gray-50 rounded-lg">
                      <span className="text-[13px] text-gray-900 font-mono">{table}</span>
                      <span className="text-sm font-semibold text-blue-500">{count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          ) : (
            <Card className="bg-white">
              <div className="flex flex-col items-center justify-center py-12 px-6 gap-3">
                <span className="text-5xl leading-none">🗄️</span>
                <p className="text-sm text-gray-600 m-0">데이터베이스 정보를 불러올 수 없습니다.</p>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* 로그 뷰어 */}
      {activeSection === 'logs' && (
        <div className="min-h-[300px]">
          <Card className="bg-white">
            <div className="flex gap-3 mb-4 max-md:flex-col">
              <select
                value={logType}
                onChange={(e) => {
                  setLogType(e.target.value);
                  loadLogs(e.target.value);
                }}
                className="flex-1 max-w-[300px] py-2.5 px-3.5 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10 max-md:max-w-none"
              >
                <option value="pm2-error">PM2 에러 로그</option>
                <option value="nginx-access">Nginx 접속 로그</option>
                <option value="nginx-error">Nginx 에러 로그</option>
              </select>
              <Button
                variant="primary"
                onClick={() => loadLogs(logType)}
                loading={loading.logs}
              >
                새로고침
              </Button>
            </div>
            <pre className="bg-gray-900 text-green-500 p-5 rounded-lg text-xs font-mono leading-relaxed whitespace-pre-wrap break-all max-h-[500px] overflow-auto m-0">
              {logs || '로그가 없습니다.'}
            </pre>
          </Card>
        </div>
      )}

      {/* 프로세스 로그 모달 */}
      <Modal
        isOpen={!!selectedProcess}
        onClose={() => setSelectedProcess(null)}
        title={`${selectedProcess} 로그`}
        size="lg"
      >
        <pre className="bg-gray-900 text-green-500 p-5 rounded-lg text-xs font-mono leading-relaxed whitespace-pre-wrap break-all max-h-[400px] overflow-auto m-0">
          {processLogs || '로그가 없습니다.'}
        </pre>
      </Modal>

      {/* 재시작 확인 모달 */}
      <Modal
        isOpen={restartModal.open}
        onClose={() => setRestartModal({ open: false, process: null })}
        title="프로세스 재시작"
        size="sm"
      >
        <div className="text-center">
          <p className="text-[15px] text-gray-900 m-0 mb-2">{restartModal.process?.name} 프로세스를 재시작하시겠습니까?</p>
          <p className="text-[13px] text-amber-600 m-0 mb-6">서비스가 일시적으로 중단될 수 있습니다.</p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="secondary"
              onClick={() => setRestartModal({ open: false, process: null })}
            >
              취소
            </Button>
            <Button
              variant="warning"
              onClick={handleRestartConfirm}
              loading={restartLoading}
            >
              재시작
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ServerMonitor;
